import type {Editor, JSONContent} from "@tiptap/core";
import {history} from "@tiptap/pm/history";
import {
    PRINTOUT_FOOTER_NODE_NAME,
    PRINTOUT_HEADER_NODE_NAME,
} from "../blocks/headerFooter/printoutRegionOptions";

const isEmptyParagraph = (node?: JSONContent) => (
    node?.type === 'paragraph' && (!node.content || node.content.length === 0)
);

const getBodyBounds = (content: JSONContent[]) => ({
    end: content[content.length - 1]?.type === PRINTOUT_FOOTER_NODE_NAME ? content.length - 1 : content.length,
    start: content[0]?.type === PRINTOUT_HEADER_NODE_NAME ? 1 : 0,
});

// Older layouts often end in a container immediately before the footer. Add an editor-only
// paragraph there so the next body line has a reliable caret target.
export const withPrintoutTrailingBodyParagraph = (document: JSONContent): JSONContent => {
    const content = [...(document.content ?? [])];
    const {end} = getBodyBounds(content);

    if (isEmptyParagraph(content[end - 1])) return document;

    content.splice(end, 0, {type: 'paragraph'});
    return {...document, content};
};

// The editor always recreates this affordance. Do not make an unused line consume print space.
export const withoutPrintoutTrailingBodyParagraph = (document: JSONContent): JSONContent => {
    const content = [...(document.content ?? [])];
    const {end, start} = getBodyBounds(content);

    if (end - start <= 1 || !isEmptyParagraph(content[end - 1])) return document;

    content.splice(end - 1, 1);
    return {...document, content};
};

// setContent replaces child nodes but does not copy the incoming document's attributes.
export const setPrintoutEditorDocument = (editor: Editor, content: JSONContent, emitUpdate = true) => {
    const editorContent = withPrintoutTrailingBodyParagraph(content);

    editor.chain()
        .setContent(editorContent, {emitUpdate})
        .command(({tr}) => {
            const document = editor.schema.nodeFromJSON(editorContent);
            Object.entries(document.attrs).forEach(([name, value]) => tr.setDocAttribute(name, value));
            return true;
        })
        .run();
};

// Language switches must not carry undo history between documents.
export const loadPrintoutEditorDocument = (editor: Editor, content: JSONContent) => {
    const freshHistory = history({depth: 100, newGroupDelay: 500});
    const historyIndex = editor.state.plugins.findIndex(plugin => (
        plugin.spec.key === freshHistory.spec.key
    ));

    if (historyIndex < 0) {
        setPrintoutEditorDocument(editor, content, false);
        return;
    }

    editor.unregisterPlugin("history");

    try {
        setPrintoutEditorDocument(editor, content, false);
    } finally {
        if (!editor.isDestroyed) {
            editor.registerPlugin(
                freshHistory,
                (historyPlugin, plugins) => [
                    ...plugins.slice(0, historyIndex),
                    historyPlugin,
                    ...plugins.slice(historyIndex),
                ],
            );
        }
    }

    if (!editor.isDestroyed) {
        editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
    }
};
